import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-03.svg";
import { TrackingPageStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { AutomationForgeElementType } from "@redotech/redo-model/forge/marketing-automation-forge-types";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { isDef } from "@redotech/util/checks";
import { Handle, Position } from "@xyflow/react";
import { memo } from "react";
import { FlowEdge, NodeTypes, TypedFlowNode } from "../flow-converter";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as css from "./node.module.css";

export interface TrackingPageNodeData {
  step: PartialWithRequiredType<TrackingPageStep>;
  deleteElements: (id: string) => void;
  readonly: boolean;
}

export const TrackingPageNode = memo(function TrackingPageNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: TrackingPageNodeData;
  selected?: boolean | undefined;
}) {
  return (
    <BaseNode
      containerClassName={css.checkoutNode}
      icon={
        <RedoFeaturedIcon color="success" Icon={ShoppingCartIcon} size="sm" />
      }
      onDelete={data.readonly ? undefined : () => data.deleteElements(id)}
      selected={selected}
      step={data.step}
      title="Tracking page"
    >
      <Handle id="top" position={Position.Top} type="target" />
      <Handle id="bottom" position={Position.Bottom} type="source" />
    </BaseNode>
  );
});

export function getTrackingPageEdgesAndDropZones({
  stepId,
  nextId,
  elementDropped,
  readonly,
}: {
  stepId: string;
  nextId: string | undefined;
  elementDropped: (
    elementType: AutomationForgeElementType,
    idFrom: string,
    idTo?: string,
    conditionSide?: boolean,
  ) => void;
  readonly: boolean;
}): { edges: FlowEdge[]; dropZoneNodes: TypedFlowNode[] } {
  const edges: FlowEdge[] = [];
  const dropZoneNodes: TypedFlowNode[] = [];

  if (isDef(nextId)) {
    edges.push({
      id: stepId + "_next",
      type: "edge",
      source: stepId,
      sourceHandle: "bottom",
      target: nextId,
      data: {
        idFrom: stepId,
        idTo: nextId,
        ...(!readonly ? { elementDropped } : {}),
      },
    });
  } else if (!readonly) {
    const dropZoneId = stepId + "_next";
    dropZoneNodes.push({
      id: dropZoneId,
      type: NodeTypes.DROP_ZONE,
      position: { x: 0, y: 0 },
      data: { idFrom: stepId, idTo: undefined, elementDropped },
    });
    edges.push({
      id: stepId + "_next_edge",
      type: "dropEdge",
      source: stepId,
      sourceHandle: "bottom",
      target: dropZoneId,
      data: {},
    });
  }

  return { edges, dropZoneNodes };
}
